/**
 * When the soft Android update prompt may check and appear.
 *
 * "Not now" only snoozes. The prompt returns on every cold start, and on a
 * return to the foreground once it has been off screen for an hour, for as
 * long as the installed build is behind. Nothing is persisted: a dismissal
 * must never outlive the process, or a skipped release is hidden for good.
 */

/** Minimum time off screen before a foreground resume shows the prompt again. */
export const RESUME_REPROMPT_MS = 60 * 60 * 1000

/** Avoid hammering Play / the version JSON on rapid app switches. */
export const CHECK_THROTTLE_MS = 15 * 60 * 1000

export type UpdateCheckClock = {
  /** Last check this process ran (null on cold start). */
  lastCheckAt: number | null
  /** Last time the prompt appeared or left the screen (null on cold start). */
  lastShownAt: number | null
  now: number
}

export function isUpdateCheckDue({ lastCheckAt, lastShownAt, now }: UpdateCheckClock): boolean {
  if (lastShownAt != null && now - lastShownAt < RESUME_REPROMPT_MS) return false
  if (lastCheckAt != null && now - lastCheckAt < CHECK_THROTTLE_MS) return false
  return true
}

export type UpdatePromptBlockers = {
  authLoading: boolean
  signedIn: boolean
  /** `/sign-in` or the OAuth callback. */
  onAuthRoute: boolean
  splashVisible: boolean
  /** Ringing, ringback, connecting, or live on this device. */
  callActive: boolean
  /** A session recording this device is capturing or still has open. */
  recordingActive: boolean
}

/** True while an owed prompt must stay off screen; it shows once this clears. */
export function isUpdatePromptSuppressed(blockers: UpdatePromptBlockers): boolean {
  return (
    blockers.authLoading ||
    !blockers.signedIn ||
    blockers.onAuthRoute ||
    blockers.splashVisible ||
    blockers.callActive ||
    blockers.recordingActive
  )
}
